/**
 * Admin Content Page
 *
 * Paginated list of all platform content (sources and drafts), with a type
 * filter, a text search, a created-date range, and a read-only detail view.
 * Filters are reflected in the URL query string so a filtered view can be
 * copied and shared.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  CaretRight as ChevronRight,
  Clock,
  FileText,
  Stack as Layers,
  ArrowsClockwise as RefreshCw,
  MagnifyingGlass as Search,
  User,
  Warning,
} from "@phosphor-icons/react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/Dialog";
import { Button } from "@/components/ui/Button";
import { Spinner } from "@/components/ui/Spinner";
import { useAdminContent, useAdminContentDetail } from "@/hooks/useAdmin";
import type { AdminContentItem } from "@/services/admin";
import { getErrorMessage } from "@/services/api";
import { cn } from "@/utils/cn";

const TYPES = [
  { value: undefined, label: "All" },
  { value: "source", label: "Sources" },
  { value: "draft", label: "Drafts" },
] as const;

const STATUS_COLORS: Record<string, string> = {
  published: "bg-green-100 text-green-700",
  draft: "bg-yellow-100 text-yellow-700",
  processing: "bg-blue-100 text-blue-700",
  error: "bg-red-100 text-red-700",
};

function formatDate(iso?: string) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function formatDateTime(iso?: string) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

interface ContentFilters {
  type: string | undefined;
  q: string;
  createdFrom: string;
  createdTo: string;
}

const EMPTY_FILTERS: ContentFilters = {
  type: undefined,
  q: "",
  createdFrom: "",
  createdTo: "",
};

function filtersFromParams(params: URLSearchParams): ContentFilters {
  const type = params.get("type");
  return {
    type: type === "source" || type === "draft" ? type : undefined,
    q: params.get("q") ?? "",
    createdFrom: params.get("createdFrom") ?? "",
    createdTo: params.get("createdTo") ?? "",
  };
}

function paramsFromFilters(filters: ContentFilters): URLSearchParams {
  const params = new URLSearchParams();
  if (filters.type) params.set("type", filters.type);
  if (filters.q) params.set("q", filters.q);
  if (filters.createdFrom) params.set("createdFrom", filters.createdFrom);
  if (filters.createdTo) params.set("createdTo", filters.createdTo);
  return params;
}

/**
 * Read-only detail view for one content item. The API needs the owner's
 * userId and the item's type to locate it (the id alone is not enough in a
 * single-table design keyed by owner) — both come off the list row.
 */
function ContentDetailDialog({
  item,
  onOpenChange,
}: {
  item: AdminContentItem | null;
  onOpenChange: (open: boolean) => void;
}) {
  const { data, isLoading, error } = useAdminContentDetail(
    item?.id,
    item
      ? { userId: item.userId, type: item.type as "source" | "draft" }
      : undefined
  );

  return (
    <Dialog open={item !== null} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{data?.title || item?.title || "Content"}</DialogTitle>
          <DialogDescription>
            Read-only content details, including the full body text.
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="flex h-32 items-center justify-center">
            <Spinner size="md" />
          </div>
        ) : error ? (
          <p className="text-sm text-destructive">
            {getErrorMessage(error, "Failed to load this content item.")}
          </p>
        ) : data ? (
          <div className="space-y-4">
            <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm sm:grid-cols-3">
              <div>
                <dt className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  Owner
                </dt>
                <dd className="mt-0.5">
                  <Link
                    to={`/users/${data.userId}`}
                    className="hover:text-primary hover:underline"
                  >
                    {data.owner.name || data.owner.email || data.userId}
                  </Link>
                </dd>
              </div>
              <div>
                <dt className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  Type
                </dt>
                <dd className="mt-0.5 capitalize">{data.contentType}</dd>
              </div>
              {data.platform && (
                <div>
                  <dt className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                    Platform
                  </dt>
                  <dd className="mt-0.5 capitalize">{data.platform}</dd>
                </div>
              )}
              {data.status && (
                <div>
                  <dt className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                    Status
                  </dt>
                  <dd className="mt-0.5">
                    <span
                      className={cn(
                        "inline-block rounded-full px-2.5 py-0.5 text-xs font-medium capitalize",
                        STATUS_COLORS[data.status] ??
                          "bg-zinc-100 text-zinc-700"
                      )}
                    >
                      {data.status}
                    </span>
                  </dd>
                </div>
              )}
              <div>
                <dt className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  Created
                </dt>
                <dd className="mt-0.5">{formatDateTime(data.createdAt)}</dd>
              </div>
              <div>
                <dt className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  Updated
                </dt>
                <dd className="mt-0.5">{formatDateTime(data.updatedAt)}</dd>
              </div>
            </dl>

            <div>
              <p className="mb-1.5 text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Content
              </p>
              {data.content ? (
                <pre className="max-h-96 overflow-y-auto whitespace-pre-wrap break-words rounded-lg border border-border/50 bg-muted/30 p-3 font-sans text-sm">
                  {data.content}
                </pre>
              ) : (
                <p className="text-sm text-muted-foreground">
                  This item has no body text.
                </p>
              )}
            </div>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

export function AdminContentPage() {
  const [searchParams, setSearchParams] = useSearchParams();

  const initialFilters = useMemo(() => filtersFromParams(searchParams), []); // eslint-disable-line react-hooks/exhaustive-deps

  const [qInput, setQInput] = useState(initialFilters.q);
  const [filters, setFilters] = useState<ContentFilters>(initialFilters);
  const [cursor, setCursor] = useState<string | undefined>();
  const [previousItems, setPreviousItems] = useState<AdminContentItem[]>([]);
  const [selectedItem, setSelectedItem] = useState<AdminContentItem | null>(
    null
  );

  const qTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined
  );

  const applyFilters = useCallback(
    (next: ContentFilters) => {
      setFilters(next);
      setCursor(undefined);
      setPreviousItems([]);
      setSearchParams(paramsFromFilters(next), { replace: true });
    },
    [setSearchParams]
  );

  const handleQChange = useCallback(
    (value: string) => {
      setQInput(value);
      if (qTimeoutRef.current) clearTimeout(qTimeoutRef.current);
      qTimeoutRef.current = setTimeout(() => {
        applyFilters({ ...filters, q: value });
      }, 400);
    },
    [applyFilters, filters]
  );

  useEffect(() => {
    return () => {
      if (qTimeoutRef.current) clearTimeout(qTimeoutRef.current);
    };
  }, []);

  function handleTypeChange(type: string | undefined) {
    applyFilters({ ...filters, type });
  }

  function handleCreatedFromChange(value: string) {
    applyFilters({ ...filters, createdFrom: value });
  }

  function handleCreatedToChange(value: string) {
    applyFilters({ ...filters, createdTo: value });
  }

  function handleClearFilters() {
    setQInput("");
    applyFilters(EMPTY_FILTERS);
  }

  const hasActiveFilters =
    filters.type !== undefined ||
    filters.q !== "" ||
    filters.createdFrom !== "" ||
    filters.createdTo !== "";

  const { data, isLoading, error, refetch, isFetching } = useAdminContent({
    type: filters.type,
    q: filters.q || undefined,
    createdFrom: filters.createdFrom || undefined,
    createdTo: filters.createdTo || undefined,
    cursor,
    limit: 25,
  });

  const displayItems = [...previousItems, ...(data?.items ?? [])];

  function handleLoadMore() {
    if (data?.items) {
      setPreviousItems((prev) => [...prev, ...data.items]);
    }
    setCursor(data?.cursor);
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-xl font-bold tracking-tight sm:text-2xl">
            Content
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Browse all platform content items.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => refetch()}
          disabled={isFetching}
        >
          <RefreshCw
            className={cn("mr-1.5 h-3.5 w-3.5", isFetching && "animate-spin")}
          />
          Refresh
        </Button>
      </div>

      {/* Type filter */}
      <div className="flex gap-2">
        {TYPES.map(({ value, label }) => (
          <button
            key={label}
            onClick={() => handleTypeChange(value)}
            className={cn(
              "rounded-lg px-3.5 py-2 text-sm font-medium transition-colors",
              filters.type === value
                ? "bg-primary text-primary-foreground"
                : "bg-muted text-muted-foreground hover:bg-muted/80"
            )}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Search + date range */}
      <div className="flex flex-wrap items-end gap-3">
        <div className="relative max-w-md flex-1 space-y-1">
          <label
            htmlFor="content-search"
            className="text-xs font-medium text-muted-foreground"
          >
            Search
          </label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              id="content-search"
              type="search"
              value={qInput}
              onChange={(e) => handleQChange(e.target.value)}
              placeholder="Search title or body..."
              className="w-full rounded-lg border border-border bg-card py-2.5 pl-10 pr-4 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
        </div>

        <div className="space-y-1">
          <label
            htmlFor="content-created-from"
            className="text-xs font-medium text-muted-foreground"
          >
            Created from
          </label>
          <input
            id="content-created-from"
            type="date"
            value={filters.createdFrom}
            max={filters.createdTo || undefined}
            onChange={(e) => handleCreatedFromChange(e.target.value)}
            className="block rounded-lg border border-border bg-card px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>

        <div className="space-y-1">
          <label
            htmlFor="content-created-to"
            className="text-xs font-medium text-muted-foreground"
          >
            Created to
          </label>
          <input
            id="content-created-to"
            type="date"
            value={filters.createdTo}
            min={filters.createdFrom || undefined}
            onChange={(e) => handleCreatedToChange(e.target.value)}
            className="block rounded-lg border border-border bg-card px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>

        {hasActiveFilters && (
          <Button variant="ghost" size="sm" onClick={handleClearFilters}>
            Clear filters
          </Button>
        )}
      </div>

      {/* Content list */}
      {isLoading ? (
        <div className="flex h-40 items-center justify-center">
          <Spinner size="md" />
        </div>
      ) : error ? (
        <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-4 text-center text-sm text-destructive">
          {getErrorMessage(error, "Failed to load content.")}
        </div>
      ) : (
        <>
          {data?.truncated && (
            <p
              role="status"
              className="flex items-center gap-2 rounded-lg border border-amber-300/60 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-600/30 dark:bg-amber-900/10 dark:text-amber-200"
            >
              <Warning className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
              Showing partial results — the scan stopped early. Load more to
              keep going.
            </p>
          )}

          <div className="space-y-2">
            {displayItems.map((item) => (
              <div
                key={item.id}
                role="link"
                tabIndex={0}
                className="flex cursor-pointer flex-col gap-2 rounded-xl border border-border/50 bg-card p-4 transition-colors hover:bg-muted/20 sm:flex-row sm:items-center sm:justify-between"
                onClick={() => setSelectedItem(item)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    setSelectedItem(item);
                  }
                }}
              >
                <div className="flex items-start gap-3">
                  <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-muted">
                    {item.type === "source" ? (
                      <FileText className="h-4 w-4 text-muted-foreground" />
                    ) : (
                      <Layers className="h-4 w-4 text-muted-foreground" />
                    )}
                  </div>
                  <div className="min-w-0">
                    <p className="truncate font-medium">
                      {item.title || "Untitled"}
                    </p>
                    <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                      <span
                        className="inline-flex items-center gap-1"
                        title={item.userId}
                      >
                        <User className="h-3 w-3" />
                        {item.userName ||
                          item.userEmail ||
                          `${item.userId.slice(0, 8)}...`}
                      </span>
                      <span className="inline-flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {formatDate(item.createdAt)}
                      </span>
                      {item.platform && (
                        <span className="capitalize">{item.platform}</span>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2 sm:shrink-0">
                  <span className="rounded-full bg-muted px-2.5 py-0.5 text-xs font-medium capitalize text-muted-foreground">
                    {item.type}
                  </span>
                  {item.status && (
                    <span
                      className={cn(
                        "rounded-full px-2.5 py-0.5 text-xs font-medium capitalize",
                        STATUS_COLORS[item.status] ??
                          "bg-zinc-100 text-zinc-700"
                      )}
                    >
                      {item.status}
                    </span>
                  )}
                  <ChevronRight className="hidden h-4 w-4 text-muted-foreground sm:block" />
                </div>
              </div>
            ))}

            {displayItems.length === 0 && !isLoading && (
              <div className="py-8 text-center text-sm text-muted-foreground">
                {hasActiveFilters
                  ? "No content matches your filters."
                  : "No content items found."}
              </div>
            )}
          </div>

          {data?.cursor && (
            <div className="flex justify-center">
              <Button variant="outline" size="sm" onClick={handleLoadMore}>
                Load More
              </Button>
            </div>
          )}
        </>
      )}

      <ContentDetailDialog
        item={selectedItem}
        onOpenChange={(open) => {
          if (!open) setSelectedItem(null);
        }}
      />
    </div>
  );
}
